import axios from 'axios';

// ─── Production API Base ──────────────────────────────────────────────────────
const API = axios.create({
  baseURL: 'https://yaatri-5v36.onrender.com/api/trains',
  timeout: 30_000,
  headers: { 'Content-Type': 'application/json' },
});

// ─── Types ────────────────────────────────────────────────────────────────────

export interface Train {
  _id: string;
  train_number: string;
  name: string;
  from_station_code: string;
  from_station_name: string;
  to_station_code: string;
  to_station_name: string;
  zone: string;
  distance: number;
  duration_h: number;
  duration_m: number;
  third_ac: boolean;
  second_ac: boolean;
  first_ac: boolean;
  first_class: boolean;
  sleeper: boolean;
  chair_car: boolean;
  arrival: string;
  departure: string;
  return_train?: string;
  train_type: string;
  classes?: string;
  geometry?: {
    type: string;
    coordinates: [number, number][];
  };
}

export interface PaginatedResponse {
  success: boolean;
  total: number;
  page: number;
  totalPages: number;
  limit: number;
  data: Train[];
}

export interface TrainsQuery {
  page?: number;
  limit?: number;
  search?: string;
  zone?: string;
  train_type?: string;
  class?: string;
  sort?: string;
}

export interface StatsData {
  total: number;
  zones: { _id: string; count: number }[];
  types: { _id: string; count: number }[];
}

export interface Station {
  code: string;
  name: string;
}

// ─── API Functions ────────────────────────────────────────────────────────────

/** GET /api/trains/stats */
export const fetchStats = async (): Promise<StatsData> => {
  const { data } = await API.get<{ success: boolean; data: StatsData }>('/stats');
  return data.data;
};

/** GET /api/trains */
export const fetchTrains = async (params: TrainsQuery = {}): Promise<PaginatedResponse> => {
  const { data } = await API.get<PaginatedResponse>('/', {
    params: { limit: 50, ...params },
  });
  return data;
};

/** GET /api/trains/:trainNumber */
export const fetchTrainByNumber = async (trainNumber: string): Promise<Train> => {
  const { data } = await API.get<{ success: boolean; data: Train }>(`/${trainNumber}`);
  return data.data;
};

/** GET /api/trains/:trainNumber/route */
export const fetchTrainRoute = async (trainNumber: string): Promise<Train> => {
  const { data } = await API.get<{ success: boolean; data: Train }>(`/${trainNumber}/route`);
  return data.data;
};

/** GET /api/trains/search?from=&to= */
export const fetchTrainsBetweenStations = async (from: string, to: string): Promise<Train[]> => {
  const { data } = await API.get<{ success: boolean; count: number; data: Train[] }>('/search', {
    params: { from, to },
  });
  return data.data ?? [];
};

/** GET /api/trains/stations?q= */
export const fetchStations = async (q: string): Promise<Station[]> => {
  const { data } = await API.get<{ success: boolean; data: Station[] }>('/stations', {
    params: { q },
  });
  return data.data ?? [];
};

/** GET /api/trains/zones */
export const fetchZones = async (): Promise<string[]> => {
  const { data } = await API.get<{ success: boolean; data: string[] }>('/zones');
  return data.data ?? [];
};

/** GET /api/trains/train-types */
export const fetchTrainTypes = async (): Promise<string[]> => {
  const { data } = await API.get<{ success: boolean; data: string[] }>('/train-types');
  return data.data ?? [];
};

/** GET /api/trains/zone/:zone */
export const fetchTrainsByZone = async (
  zone: string,
  page = 1,
  limit = 50
): Promise<PaginatedResponse> => {
  const { data } = await API.get<PaginatedResponse>(`/zone/${zone}`, {
    params: { page, limit },
  });
  return data;
};

/** GET /api/trains/:trainNumber/similar */
export const fetchSimilarTrains = async (trainNumber: string): Promise<Train[]> => {
  const { data } = await API.get<{ success: boolean; count: number; data: Train[] }>(
    `/${trainNumber}/similar`
  );
  return data.data ?? [];
};
